import type { ReactElement } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Box, Button } from '@chakra-ui/react'
import { ChevronRight, Circle } from 'lucide-react'
import type { AppAbility } from '@/shared/lib/casl'
import { pathMatchesNavPath } from '@/shared/utils/navPathMatch'
import { NavItem } from './NavItem'
import { getFirstReadableEntry } from './navData'
import type { INavData } from './navData'

/** A nav entry with search params is active only when the current URL carries the same ones. */
function searchMatches(
  item: INavData,
  currentSearch: Record<string, unknown>,
): boolean {
  if (!item.search) return true
  return Object.entries(item.search).every(
    ([key, value]) => currentSearch[key] === value,
  )
}

function branchContainsActivePath(
  items: INavData[] | undefined,
  currentPath: string,
  abilities: AppAbility,
): boolean {
  if (!items?.length) return false

  for (const node of items) {
    if (node.hide) continue
    if (abilities.cannot('read', node.key)) continue
    if (node.path && pathMatchesNavPath(currentPath, node.path)) return true
    if (branchContainsActivePath(node.children, currentPath, abilities))
      return true
  }

  return false
}

/**
 * A nav entry that owns sub-entries. The header links to the first sub-page
 * the user's roles can read (see `getFirstReadableEntry`), and is a plain
 * toggle only when there is none. Sub-entries
 * collapse: entering the section expands them, leaving collapses them again,
 * and the chevron lets the user override either way.
 */
function NavGroup({
  item,
  depth,
  open,
  currentPath,
  currentSearch,
  abilities,
  t,
}: {
  item: INavData
  depth: number
  open: boolean
  currentPath: string
  currentSearch: Record<string, unknown>
  abilities: AppAbility
  t: (key: string) => string
}) {
  const pad = depth * 10
  const label = t(`nav.${item.labelKey ?? item.key}`)
  const routeActive = useMemo(
    () =>
      item.path
        ? pathMatchesNavPath(currentPath, item.path)
        : branchContainsActivePath(item.children, currentPath, abilities),
    [item.path, item.children, currentPath, abilities],
  )
  const [userOpen, setUserOpen] = useState<boolean | null>(null)
  const expanded = userOpen ?? routeActive

  // Follow the route again whenever the section is entered or left.
  useEffect(() => setUserOpen(null), [routeActive])

  const toggle = () => setUserOpen(!expanded)

  const entry = useMemo(
    () => getFirstReadableEntry(item, abilities),
    [item, abilities],
  )

  return (
    <>
      <Box pl={`${pad}px`} position="relative">
        <NavItem
          open={open}
          icon={item.icon ?? Circle}
          label={label}
          path={entry?.path}
          search={entry?.search}
          active={routeActive && searchMatches(item, currentSearch)}
          onClick={entry ? undefined : toggle}
        />
        {open ? (
          <Button
            type="button"
            variant="ghost"
            aria-expanded={expanded}
            aria-label={label}
            onClick={toggle}
            position="absolute"
            top="50%"
            right="8px"
            transform="translateY(-50%)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minW="24px"
            w="24px"
            h="24px"
            p={0}
            borderRadius="md"
            border="none"
            bg="transparent"
            color="inherit"
            cursor="pointer"
            _hover={{ bg: 'rgba(127, 127, 127, 0.18)' }}
          >
            <ChevronRight
              size={16}
              style={{
                transform: expanded ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.15s ease',
              }}
              aria-hidden
            />
          </Button>
        ) : null}
      </Box>
      {expanded
        ? NavBranch({
            open,
            items: item.children ?? [],
            depth: depth + 1,
            currentPath,
            currentSearch,
            abilities,
            t,
          })
        : null}
    </>
  )
}

interface NavBranchProps {
  open: boolean
  items: INavData[]
  depth?: number
  currentPath: string
  currentSearch: Record<string, unknown>
  abilities: AppAbility
  t: (key: string) => string
}

export function NavBranch({
  open,
  items,
  depth = 0,
  currentPath,
  currentSearch,
  abilities,
  t,
}: NavBranchProps): ReactElement[] {
  return items.flatMap((item, idx): ReactElement[] => {
    if (item.hide || item.key === 'settings') return []
    if (abilities.cannot('read', item.key)) return []
    // Sub-entries are label-only — nothing readable is left in a collapsed sidebar.
    if (depth > 0 && !open) return []

    const key = `${item.labelKey ?? item.key}-${depth}-${idx}`
    const pad = depth * 10

    if (item.children?.length) {
      return [
        <NavGroup
          key={key}
          open={open}
          item={item}
          depth={depth}
          currentPath={currentPath}
          currentSearch={currentSearch}
          abilities={abilities}
          t={t}
        />,
      ]
    }

    if (!item.path) return []

    const isSubItem = depth > 0

    return [
      <Box key={key} pl={`${pad}px`}>
        <NavItem
          open={open}
          icon={isSubItem ? undefined : (item.icon ?? Circle)}
          sub={isSubItem}
          label={t(`nav.${item.labelKey ?? item.key}`)}
          path={item.path}
          search={item.search}
          active={
            pathMatchesNavPath(currentPath, item.path) &&
            searchMatches(item, currentSearch)
          }
        />
      </Box>,
    ]
  })
}
